import { useUser } from "@clerk/clerk-react";
import { Mail, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { clerkEnabled } from "@/services/clerk";
import { sectionTitleClass } from "@/lib/typography";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { describeClerkError as describe } from "@/lib/clerkErrors";

type User = NonNullable<ReturnType<typeof useUser>["user"]>;
type EmailAddress = User["emailAddresses"][number];

/**
 * The addresses somebody can sign in with, and the way to add one.
 *
 * Lives here rather than on the Account page alone because the registration
 * form needs it too: the form asks which address to publish, and an applicant
 * whose only address is the one they signed up with has nowhere to add the
 * shared info@ they want in the directory. Sending them to another page in the
 * middle of a form is how a form gets abandoned.
 *
 * It is not on the profile page. The profile is what other members see; this
 * is how you get in, which is an account matter.
 */
export function EmailAddresses(props: { user?: User | null }) {
  const { user } = useUser();
  const account = props.user ?? user;
  if (!clerkEnabled || !account) return null;
  return <List user={account} />;
}

function List({ user }: { user: User }) {
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  // The empty row at the foot of the list, which only exists once asked for.
  // A permanent form under the addresses read as a fourth address.
  const [addingRow, setAddingRow] = useState(false);
  // The address whose code box is open, and the code typed into it.
  const [verifying, setVerifying] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const addresses = [...user.emailAddresses].sort((a, b) =>
    a.id === user.primaryEmailAddressId ? -1 : b.id === user.primaryEmailAddressId ? 1 : 0
  );

  const sendCode = async (address: EmailAddress) => {
    await address.prepareVerification({ strategy: "email_code" });
    setVerifying(address.id);
    setCode("");
    toast.success(`We emailed a code to ${address.emailAddress}`);
  };

  const add = async () => {
    const email = draft.trim();
    if (!email) return;
    setAdding(true);
    try {
      const address = await user.createEmailAddress({ email });
      setDraft("");
      setAddingRow(false);
      // Straight into verification: an address nobody has confirmed cannot be
      // made primary and cannot be signed in with, so leaving it unverified
      // is leaving it useless.
      await sendCode(address);
    } catch (err) {
      toast.error(describe(err));
    } finally {
      setAdding(false);
    }
  };

  const confirm = async (address: EmailAddress) => {
    setBusy(address.id);
    try {
      const result = await address.attemptVerification({ code: code.trim() });
      if (result.verification.status === "verified") {
        setVerifying(null);
        toast.success("Address verified. You can sign in with it now.");
      } else {
        toast.error("That code did not verify. Check it and try again.");
      }
    } catch (err) {
      toast.error(describe(err));
    } finally {
      setBusy(null);
    }
  };

  const makePrimary = async (address: EmailAddress) => {
    setBusy(address.id);
    try {
      await user.update({ primaryEmailAddressId: address.id });
      toast.success(`${address.emailAddress} is now your primary address`);
    } catch (err) {
      toast.error(describe(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (address: EmailAddress) => {
    setBusy(address.id);
    try {
      await address.destroy();
      if (verifying === address.id) setVerifying(null);
      toast.success("Address removed");
    } catch (err) {
      toast.error(describe(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Email addresses</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground text-sm">
          Sign in with any of these. Whichever you use, it is the same
          membership, so an address for each organization you look after is
          fine. The primary one is where we write to you.
        </p>
        <ul className="divide-y">
          {addresses.map((address) => {
            const primary = address.id === user.primaryEmailAddressId;
            const verified = address.verification.status === "verified";
            const working = busy === address.id;
            return (
              <li key={address.id} className="space-y-3 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Mail className="text-muted-foreground size-4" />
                  <span className="text-sm">{address.emailAddress}</span>
                  {primary && <Badge variant="secondary">Primary</Badge>}
                  {!verified && <Badge variant="outline">Unverified</Badge>}
                  <span className="ml-auto flex gap-1">
                    {!verified && verifying !== address.id && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={working}
                        onClick={() => sendCode(address).catch((e) => toast.error(describe(e)))}
                      >
                        Verify
                      </Button>
                    )}
                    {verified && !primary && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={working}
                        onClick={() => makePrimary(address)}
                      >
                        Make primary
                      </Button>
                    )}
                    {!primary && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={working}
                        onClick={() => remove(address)}
                      >
                        <Trash2 data-slot="icon" /> Remove
                      </Button>
                    )}
                  </span>
                </div>
                {verifying === address.id && (
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="space-y-2">
                      <Label htmlFor={`code-${address.id}`}>
                        Code we emailed to {address.emailAddress}
                      </Label>
                      <Input
                        id={`code-${address.id}`}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="w-40"
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={working || !code.trim()}
                      onClick={() => confirm(address)}
                    >
                      {working && <Spinner data-slot="icon" />} Confirm
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => sendCode(address).catch((e) => toast.error(describe(e)))}
                    >
                      Send again
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {addingRow ? (
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="new-email"
              type="email"
              autoComplete="email"
              aria-label="New email address"
              placeholder="you@example.org"
              className="grow"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            <Button type="button" disabled={adding || !draft.trim()} onClick={add}>
              {adding && <Spinner data-slot="icon" />}
              Save
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setAddingRow(false);
                setDraft("");
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button type="button" variant="outline" onClick={() => setAddingRow(true)}>
            <Plus data-slot="icon" /> Add Address
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default EmailAddresses;
